import { CircularProgress } from '@shared/components';
import { ChatViewContextGetter } from '@shared/contexts/ChatWindowContext';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { ChatsPage } from '@shared/features/chats-page';
import { ConciergeContextProvider } from '@shared/plugin/contexts/ConciergeContext';
import { hasChatsAccess } from '@shared/utils/permissions';
import { QueryParamName } from 'clerk_common/constants/urls/queryParams';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { PageChat } from './pageChat';
import { useApplyPageChat } from './useApplyPageChat';

type PluginChatsPageProps = {
  getSuggestionContext?: ChatViewContextGetter;
  getViewContext?: ChatViewContextGetter;
  inputBanner?: ReactNode;
  onNewMessageInSelectedConversation?: () => void;
  onUserConversationSelect?: (conversationId: string | null) => void;
  pageChat?: PageChat;
};

export function PluginChatsPage({
  getSuggestionContext,
  getViewContext,
  inputBanner,
  onNewMessageInSelectedConversation,
  onUserConversationSelect,
  pageChat,
}: PluginChatsPageProps) {
  const { hasVee, isVoomaAdmin, loading } = useMeContext();

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <CircularProgress />
      </div>
    );
  }
  if (!hasChatsAccess({ hasVee, isVoomaAdmin })) {
    return <Navigate to="/" replace />;
  }

  return (
    <ConciergeContextProvider>
      <PluginChatsPageContent
        getSuggestionContext={getSuggestionContext}
        getViewContext={getViewContext}
        inputBanner={inputBanner}
        onNewMessageInSelectedConversation={onNewMessageInSelectedConversation}
        onUserConversationSelect={onUserConversationSelect}
        pageChat={pageChat}
      />
    </ConciergeContextProvider>
  );
}

function PluginChatsPageContent({
  getSuggestionContext,
  getViewContext,
  inputBanner,
  onNewMessageInSelectedConversation,
  onUserConversationSelect,
  pageChat,
}: PluginChatsPageProps) {
  const { defaultOrgId } = useMeContext();
  const latestOrgId = useRef(defaultOrgId);
  latestOrgId.current = defaultOrgId;
  const committedOrgId = useRef(defaultOrgId);
  useEffect(() => {
    committedOrgId.current = defaultOrgId;
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const conversationId = searchParams.get(QueryParamName.CHAT_CONVERSATION_ID);
  const handleConversationSelect = useCallback(
    (selectedConversationId: string | null) => {
      setSearchParams(
        (currentSearchParams) => {
          const nextSearchParams = new URLSearchParams(currentSearchParams);
          if (selectedConversationId) {
            nextSearchParams.set(
              QueryParamName.CHAT_CONVERSATION_ID,
              selectedConversationId
            );
          } else {
            nextSearchParams.delete(QueryParamName.CHAT_CONVERSATION_ID);
          }
          return nextSearchParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const [newChatRequestCount, setNewChatRequestCount] = useState(0);
  const requestNewChat = useCallback(
    () => setNewChatRequestCount((count) => count + 1),
    []
  );
  const { isChoosingChat, noteUserSelection } = useApplyPageChat({
    conversationId,
    pageChat,
    onConversationSelect: handleConversationSelect,
    onNewChat: requestNewChat,
  });
  const handleUserConversationSelect = useCallback(
    (selectedConversationId: string | null) => {
      const isOrganizationReset =
        committedOrgId.current !== latestOrgId.current;
      if (!isOrganizationReset) {
        noteUserSelection();
        onUserConversationSelect?.(selectedConversationId);
      }
      handleConversationSelect(selectedConversationId);
    },
    [handleConversationSelect, noteUserSelection, onUserConversationSelect]
  );

  return (
    <NuqsAdapter>
      <ChatsPage
        conversationId={conversationId}
        getSuggestionContext={getSuggestionContext}
        getViewContext={getViewContext}
        inputBanner={inputBanner}
        isCompact
        isLoadingConversation={isChoosingChat}
        newChatRequestCount={newChatRequestCount}
        onConversationSelect={handleUserConversationSelect}
        onNewMessageInSelectedConversation={onNewMessageInSelectedConversation}
      />
    </NuqsAdapter>
  );
}
